import React, { useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon,
  EnvelopeIcon,
  EyeIcon,
  PencilSquareIcon,
  ReceiptRefundIcon,
  SquaresPlusIcon,
} from '@heroicons/react/24/outline';
import { Send } from 'lucide-react';

import Dash from '@dashboard/components/Dash';
import EmailCustomerDialog from '@dashboard/components/EmailCustomerDialog';
import FieldEditDialog from '@dashboard/components/FieldEditDialog';
import Link from '@dashboard/components/Link';
import { Badge } from '@dashboard/components/ui/badge';
import {
  Action,
  ActionGroup,
  CancelChargeDialog,
  ChargeAlerts,
  ItemGroup,
  ItemRow,
  OrderAbandonmentCard,
  OrderActionsColumn,
  OrderCustomerCard,
  OrderFieldsCard,
  OrderPaymentBlock,
  OrderQueryParametersCard,
  OrderTableCard,
  resendConfirmationOffReason,
  sortDetailRows,
  TotalsList,
  actionClass,
  joinDetails,
  stripeIdRow,
  nestSetupFees,
  setupFeeSubLine,
  RecordPaymentAction,
} from '@dashboard/components/OrderPage';
import { useAdminGhosting } from '@dashboard/context/AdminGhostingContext/AdminGhostingContext';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { toast } from '@dashboard/context/ToastContext';
import { buildEmailVariables } from '@dashboard/lib/emailVariables';
import { formatDateTime } from '@dashboard/lib/format-date';
import { confirm } from '@dashboard/lib/confirm';
import { chargeEmailLogsKey, chargeKey } from '@dashboard/lib/queryKeys';
import getStripeDashboardUrl from '@dashboard/lib/stripe/get-stripe-dashboard-url';
import isAbandonedAbandonment from '@dashboard/services/abandonment/isAbandonedAbandonment';
import { formatPrice } from '@dashboard/services/formatting';
import getPageTypeLabel, { getPageTypeHeading } from '@dashboard/services/getPageTypeLabel';
import { fetcher } from '@dashboard/services/request';
import getPriceLabel from '@dashboard/services/translation/getPriceLabel';
import { getPaymentMethodLabel } from '@repo/lib';
import { getBulkOff, getBulkTier } from '@dashboard/scenes/PaymentsChargesDetail/chargeTotals';
import PaymentsBookingArchiveDialog from '../components/PaymentsBookingArchiveDialog';
import PaymentsBookingRefundDialog from '../components/PaymentsBookingRefundDialog';
import { getAddOns, getBookingTotals, getTicketRow, groupTickets } from '../bookingTotals';
import { usePageRecordHref } from '@dashboard/lib/detailLinks';
import { formatOrderId } from '@dashboard/lib/orderId';
import { bookingTicketCount } from '../useBookingGuests';

/** Tickets (by group) and Add-ons, then the totals: the old Payment, Tickets, Add-ons, Bulk discount and Coupon cards in one block. */
export const BookingItems = ({ payment }) => {
  const { currency } = payment;
  const money = amount => getPriceLabel({ amount, currency });
  const negative = amount => getPriceLabel({ amount, currency, isNegated: true });
  const groups = groupTickets(payment.tickets);
  const addOns = getAddOns(payment);
  const hasImages = addOns.some(item => item.imageUrl);
  const href = payment.page?._id ? `/pages/${payment.page._id}` : undefined;
  // A type split over two lines (a coupon priced some a cent apart) shows its canceled count once.
  const canceledLeft = { ...(payment.canceledTicketCounts ?? {}) };
  const canceledBadge = ticket => {
    const type = String(ticket.ticketType?._id ?? ticket.ticketType);
    const count = Math.min(canceledLeft[type] ?? 0, ticket.quantity ?? 0);
    if (!count) return null;
    canceledLeft[type] -= count;
    return <Badge variant="destructive">{count} canceled</Badge>;
  };

  const { rows } = getBookingTotals(payment);
  const totals = rows.map(row => {
    const value = row.amount < 0 ? negative(-row.amount) : money(row.amount);
    let label = row.label;
    // The whole-booking tier carries its tier text; the per-ticket tiers are itemised in the ticket sub-lines.
    if (row.kind === 'bulk' || row.kind === 'bulk-tickets')
      label = (
        <>
          <Link to={`/pages/${payment.page?._id}/customize/event/bulk-discounts`}>
            Bulk discount
          </Link>
          {row.kind === 'bulk'
            ? `: ${getBulkTier(payment.discount, 'tickets')}, ${getBulkOff(payment.discount, currency)}`
            : ': per ticket'}
        </>
      );
    if (row.kind === 'coupon')
      label = (
        <>
          Discount: {payment.coupon.label} (
          <Link to={`/coupons/${payment.coupon._id}`}>{payment.coupon.code}</Link>
          {payment.coupon.percentOff ? `, ${payment.coupon.percentOff}% off` : ''})
        </>
      );
    return { key: row.key, label, value, bold: row.bold, rule: row.rule };
  });

  // A ticket with its fee "included in price" reads as one price that includes the fee; one with the
  // fee "shown separately" reads at its price with the fee as a sub-line, as the confirmation email does.
  const ticketDetails = ticket => {
    const row = getTicketRow(ticket);
    const feeIncluded = row.fee > 0 && row.feeDisplay === 'included';
    return joinDetails([
      `${row.quantity} × ${money(row.originalPrice + (feeIncluded ? row.fee : 0))}`,
      feeIncluded ? `includes ${money(row.fee)} ${row.feeName.toLowerCase()}` : null,
      // The per-ticket tier's percentage and min / max quantity ride along, as the Bulk discount card's sub-table showed them.
      row.bulkOff
        ? `${money(row.bulkOff)} off each (bulk discount${
            ticket.discount?.percentOff ? `, ${ticket.discount.percentOff}%` : ''
          }${ticket.discount?.minQuantity ? `, ${getBulkTier(ticket.discount, 'tickets')}` : ''})`
        : null,
      row.couponOff ? `${money(row.couponOff)} off each (coupon)` : null,
    ]);
  };
  const ticketAmount = ticket => {
    const row = getTicketRow(ticket);
    return money(row.feeDisplay === 'separate' ? row.amount - row.feeTotal : row.amount);
  };
  const ticketFeeLine = ticket => {
    const row = getTicketRow(ticket);
    if (!(row.fee > 0 && row.feeDisplay === 'separate')) return null;
    return {
      label: row.quantity > 1 ? `${row.feeName} × ${row.quantity}` : row.feeName,
      amount: money(row.feeTotal),
    };
  };

  return (
    <>
      {groups.length > 0 && (
        <ItemGroup title="Tickets" count={bookingTicketCount(payment)}>
          {groups.map((group, groupIndex) => (
            <React.Fragment key={group._id ?? groupIndex}>
              {groups.length > 1 && (
                <li className="pt-3 text-sm font-medium text-gray-500 dark:text-muted-foreground">
                  {group.name}
                </li>
              )}
              {group.tickets.map((ticket, index) => (
                <ItemRow
                  key={ticket._id ?? index}
                  name={ticket.name}
                  details={ticketDetails(ticket)}
                  amount={ticketAmount(ticket)}
                  sub={ticketFeeLine(ticket)}
                  badge={canceledBadge(ticket)}
                />
              ))}
            </React.Fragment>
          ))}
        </ItemGroup>
      )}
      {addOns.length > 0 && (
        <ItemGroup title="Add-ons">
          {nestSetupFees(addOns).map(({ item, setupFee }, index) => (
            <ItemRow
              key={index}
              showImage={hasImages}
              imageUrl={item.imageUrl}
              name={item.name}
              href={href}
              details={`${item.quantity} × ${money(item.unitPrice)}`}
              amount={money(item.amount)}
              sub={setupFee ? setupFeeSubLine(setupFee, money) : null}
            />
          ))}
        </ItemGroup>
      )}
      <TotalsList totals={totals} />
    </>
  );
};

const PaymentsBookingsDetail = ({ payment }) => {
  const recordHref = usePageRecordHref();
  const { seller, isAdminLoggedInAsSeller, token } = useAuth();
  const { setAdminGhostingSellerId } = useAdminGhosting();
  const queryClient = useQueryClient();
  const { currency } = payment;

  useEffect(() => {
    if (payment?.seller) setAdminGhostingSellerId(payment.seller);
  }, [payment?.seller, setAdminGhostingSellerId]);

  const { mutateAsync: updateFields } = useMutation({
    mutationFn: async values =>
      fetcher(`/sellers/${seller._id}/charges/${payment._id}`, token, {
        method: 'PUT',
        body: JSON.stringify({ fields: values }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chargeKey(payment._id) });
      toast({ title: 'Fields updated' });
    },
  });

  const { mutate: sendChargeConfirmationEmail, isPending: isSendingChargeConfirmationEmail } =
    useMutation({
      mutationFn: async () =>
        fetcher(`/sellers/${seller._id}/charges/${payment._id}/send-confirmation-email`, token, {
          method: 'POST',
        }),
      onSuccess: () => toast({ title: 'Confirmation email resent' }),
      onError: error =>
        toast({
          title: 'Failed to send confirmation email',
          description: error?.message,
          type: 'error',
        }),
    });

  const handleSendChargeConfirmationEmail = async () => {
    const confirmed = await confirm('Send a new confirmation email to the customer?', {
      confirm: 'Send',
    });
    if (confirmed) sendChargeConfirmationEmail();
  };

  const { mutate: downloadTicketPDF, isPending: isDownloadingTicketPDF } = useMutation({
    mutationFn: async () =>
      fetcher(`/sellers/${seller._id}/tickets/${payment._id}/pdf`, token, { method: 'get' }),
    onSuccess: data => {
      if (data.pdfUrl == null) {
        toast({ title: 'Failed to download ticket PDF', type: 'error' });
        return;
      }
      const link = document.createElement('a');
      link.href = data.pdfUrl;
      link.download = '';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    onError: () => toast({ title: 'Failed to download ticket PDF', type: 'error' }),
  });

  // Ticket PDFs exist for four early event pages and every booking since the feature shipped.
  const hasPdf =
    payment.page?._id === '681aac984e3f8ba880f636f8' ||
    payment.page?._id === '667211f37afaf75caf59ffce' ||
    payment.page?._id === '67ff336a0ea017badff86621' ||
    payment.page?._id === '67e9ebab28eae4f818011f12' ||
    (payment?.createdAt && new Date(payment.createdAt) > new Date('2025-05-12T08:35:07.000Z'));

  const stripe = { livemode: payment.livemode, seller, isAdminLoggedInAsSeller };
  const details = [
    { label: 'Booking ID', value: formatOrderId(payment.orderId), copy: payment.orderId },
    // The Payment card's "Event name" row, now first in Details and linking to the event.
    {
      label: 'Event',
      copy: payment.page?.title || null,
      value: payment.page?.title ? (
        payment.page._id ? (
          <Link to={`/pages/${payment.page._id}`}>{payment.page.title}</Link>
        ) : (
          payment.page.title
        )
      ) : (
        <Dash />
      ),
    },
    {
      label: 'Payment method',
      value: payment.paymentMethod ? (
        getPaymentMethodLabel(payment.paymentMethod, { includeBrand: false })
      ) : (
        <Dash />
      ),
      copy: payment.paymentMethod
        ? getPaymentMethodLabel(payment.paymentMethod, { includeBrand: false })
        : null,
    },
    { label: 'SKU', value: payment.sku || <Dash />, copy: payment.sku || null },
    ...(payment.feeAmount
      ? [
          {
            label: 'Checkout Page fee',
            value: formatPrice({ amount: payment.feeAmount, currency }),
          },
        ]
      : []),
    {
      label: 'Customer ID',
      value: payment.customer ? `${payment.customer._id}` : <Dash />,
      copy: payment.customer ? `${payment.customer._id}` : null,
    },
    { label: 'Payment ID', value: `${payment._id}` },
    stripeIdRow({
      label: 'Stripe customer ID',
      id: payment.customer?.stripeCustomerId,
      path: `/customers/${payment.customer?.stripeCustomerId}`,
      ...stripe,
    }),
    stripeIdRow({
      label: 'Stripe charge ID',
      id: payment.chargeId,
      path: `/payments/${payment.chargeId}`,
      ...stripe,
    }),
    { label: 'Payment type', value: 'One-time' },
    {
      label: 'Prefilled',
      value: payment.isPrefilled ? 'Is prefilled' : '—',
      copy: payment.isPrefilled ? 'Is prefilled' : null,
    },
    { label: 'Created', value: formatDateTime(payment.createdAt) },
  ];

  const abandoned = isAbandonedAbandonment(payment);
  const resendOffReason = resendConfirmationOffReason(payment, 'booking');
  const stripeOff = !payment.livemode || !payment.chargeId;
  const canRefund = !payment.isComplimentary && payment.stripePaymentIntentId;

  return (
    <>
      <ChargeAlerts
        payment={payment}
        canceledTitle="Booking canceled"
        archivedTitle="Booking archived"
        showCanceledAt
      />

      <div className="flex flex-col lg:flex-row max-w-5xl items-start justify-center">
        <div className="order-2 lg:order-1 w-full lg:pr-8">
          <OrderPaymentBlock>
            <BookingItems payment={payment} />
          </OrderPaymentBlock>
          <OrderAbandonmentCard record={payment} seller={seller} />
          <OrderCustomerCard customer={payment.customer} />
          <OrderFieldsCard
            fields={payment.fields}
            page={payment.page}
            updateFields={updateFields}
            groupAddresses={false}
          />
          <OrderTableCard title="Details" rows={sortDetailRows(details)} />
          <OrderQueryParametersCard queryParameters={payment.queryParameters} />
        </div>

        <div className="order-1 lg:order-2 w-full lg:w-72 shrink-0 px-4 pt-3 lg:pt-[52px]">
          <OrderActionsColumn>
            <ActionGroup title="Booking">
              <CancelChargeDialog payment={payment} noun="booking" />
              <PaymentsBookingArchiveDialog payment={payment} />
              <Action
                icon={ArrowDownTrayIcon}
                label="Download ticket PDF"
                onClick={hasPdf ? () => downloadTicketPDF() : undefined}
                off={!hasPdf || abandoned}
                loading={isDownloadingTicketPDF}
                title={
                  !hasPdf
                    ? 'No ticket PDF for this booking'
                    : abandoned
                      ? 'This checkout was abandoned'
                      : undefined
                }
              />
            </ActionGroup>
            <ActionGroup title="Payment">
              <RecordPaymentAction payment={payment} noun="booking" />
              {canRefund ? (
                <PaymentsBookingRefundDialog payment={payment} />
              ) : (
                <Action
                  icon={ReceiptRefundIcon}
                  label="Refund booking"
                  off
                  title={
                    payment.isComplimentary
                      ? 'A complimentary booking has nothing to refund'
                      : 'No Stripe payment to refund'
                  }
                />
              )}
              <Action
                icon={ArrowTopRightOnSquareIcon}
                label="View in Stripe"
                to={getStripeDashboardUrl(`/payments/${payment.chargeId}`, {
                  seller,
                  isAdminLoggedInAsSeller,
                })}
                blank
                off={stripeOff}
                title="Only for live payments"
              />
            </ActionGroup>
            <ActionGroup title="Email">
              <Action
                icon={EnvelopeIcon}
                label="Resend confirmation"
                onClick={handleSendChargeConfirmationEmail}
                off={Boolean(resendOffReason)}
                title={resendOffReason}
                loading={isSendingChargeConfirmationEmail}
              />
              {payment.customer?.email ? (
                <EmailCustomerDialog
                  customerEmail={payment.customer.email}
                  livemode={payment.livemode}
                  objectLabel="bookings"
                  endpoint={`/sellers/${seller._id}/charges/${payment._id}/send-email`}
                  variables={buildEmailVariables({ page: payment.page })}
                  queryKey={chargeEmailLogsKey(payment._id)}
                  emailsPath={`/bookings/${payment._id}/emails`}
                />
              ) : (
                <Action
                  icon={Send}
                  label="Email customer"
                  off
                  title="The customer has no email address"
                />
              )}
            </ActionGroup>
            <ActionGroup title="Customer">
              <Action
                icon={EyeIcon}
                label="View customer"
                to={payment.customer ? recordHref('customers', payment.customer._id) : '#'}
                off={!payment.customer}
                title="No customer on this booking"
              />
            </ActionGroup>
            <ActionGroup title="Fields">
              <FieldEditDialog
                fields={payment.fields}
                page={payment.page}
                updateFields={updateFields}
                trigger={
                  <Link className={actionClass(false, 'cursor-pointer')} onClick={() => {}}>
                    <>
                      <PencilSquareIcon className="w-4 h-4" />
                      Edit fields
                    </>
                  </Link>
                }
              />
            </ActionGroup>
            <ActionGroup title={getPageTypeHeading(payment.page)}>
              <Action
                icon={SquaresPlusIcon}
                label={`View ${getPageTypeLabel(payment.page)}`}
                to={payment.page?._id ? `/pages/${payment.page._id}` : '#'}
                off={!payment.page?._id}
                title="This booking has no event page"
              />
            </ActionGroup>
          </OrderActionsColumn>
        </div>
      </div>
    </>
  );
};

export default PaymentsBookingsDetail;
